import type { IntrinsicSystemProps, MotionPropsFor } from '@hover/blueprint';
import {
  Center,
  LoadingState,
  calc,
  cssVar,
  forwardRef,
  motion,
  onSwipe,
  system,
} from '@hover/blueprint';
import { AnimatePresence } from 'framer-motion';
import React from 'react';

import { altStyle } from '../../helpers';
import {
  useGenerateImageLoadingState,
  useIsFullscreenImageOpen,
  useViewportSize,
} from '../../hooks';
import type {
  AiOrSampleDisplayImage,
  AiOrSampleGeneratedImage,
} from '../../types';
import { GeneratedImagePlaceholder } from '../GeneratedImagePlaceholder';

import { latestFullscreenImage, useFullscreenImageContents } from './context';
import {
  getFullscreenImageLayoutId,
  layoutBreakpoint,
  useFullscreenImageBreakpointValue,
  useFullscreenTransition,
} from './helpers';
import type { ImagePair } from './useImagePair';

type MotionImageProps = MotionPropsFor<IntrinsicSystemProps<'img'>>;

/**
 * Gap between images
 *
 * @remarks this is necessary because the images are positioned absolutely
 * relative to the current image using `translate`
 */
const $gap = cssVar('image-gap');
const $gapPadding = calc.divide($gap, 2);

export { $gap as $carouselImageGap };

const toggleImagePosition = {
  height: '100%',
  width: calc.subtract('100%', $gap),
  top: 0,
  left: $gapPadding,
} satisfies IntrinsicSystemProps<'img'>;

interface ImageProps extends MotionImageProps {}

const Image = forwardRef<ImageProps, 'img'>((props, ref) => {
  const { height } = useViewportSize();

  return (
    <motion.img
      borderRadius={{ base: 0, [layoutBreakpoint]: '300' }}
      display="block"
      maxHeight={{
        base: calc.subtract(height, '200px'),
        [layoutBreakpoint]: calc.subtract(height, '300px'),
      }}
      maxWidth="100%"
      minWidth={{ desktop: '750' }}
      objectFit="cover"
      width={{ base: '100vw', [layoutBreakpoint]: 'auto' }}
      {...props}
      ref={ref}
    />
  );
});

interface GeneratedImageProps extends ImageProps {
  placeholderSrc?: string;
  image: AiOrSampleGeneratedImage;
  referenceImage?: AiOrSampleGeneratedImage;
}

const GeneratedImage = ({
  image,
  placeholderSrc,
  referenceImage,
  ...props
}: GeneratedImageProps) => {
  const {
    imageProps,
    imageVariants: { loaded, generating },
    isGeneratedLoading,
  } = useGenerateImageLoadingState({
    consumerId: 'fullscreen-carousel-image',
    image,
    watermarkImage: true,
  });

  return (
    <AnimatePresence initial={false} mode="popLayout">
      {isGeneratedLoading ? (
        <motion.div
          borderRadius={{ base: 0, [layoutBreakpoint]: '300' }}
          exit={{ opacity: 0 }}
          overflow="hidden"
          position="relative"
          {...props}
        >
          <GeneratedImagePlaceholder
            background="lightAlpha.200"
            key="placeholder"
            position="relative"
            src={placeholderSrc}
          >
            <Image
              aria-hidden
              src={referenceImage?.generatedImagePath}
              visibility="hidden"
            />
          </GeneratedImagePlaceholder>
          <LoadingState
            background="lightAlpha.500"
            color="neutral.900"
            isLoading={isGeneratedLoading}
            size={{ base: 'medium', tablet: 'large', desktop: 'huge' }}
          />
        </motion.div>
      ) : (
        <Image
          {...props}
          {...imageProps}
          animate={loaded}
          initial={generating}
          key="generated-image"
        />
      )}
    </AnimatePresence>
  );
};

interface AdjacentImageProps
  extends MotionPropsFor<IntrinsicSystemProps<'div'>>,
    Pick<GeneratedImageProps, 'referenceImage'> {
  image: AiOrSampleGeneratedImage;
}

const AdjacentImage = ({
  image,
  referenceImage,
  ...props
}: AdjacentImageProps) => (
  <motion.div
    animate={{ opacity: 1 }}
    initial={{ opacity: 0 }}
    position="absolute"
    px={$gapPadding}
    transition={{ duration: 0.25 }}
    {...props}
  >
    <GeneratedImage
      aria-hidden
      display={{ base: 'block', [layoutBreakpoint]: 'none' }}
      image={image}
      referenceImage={referenceImage}
    />
  </motion.div>
);

/**
 * @remarks we must receive `FullscreenImageContentsContext` props here
 * instead of reading the state from context so the image doesn't update
 * when transitioning out with `AnimatePresence` when unmounting
 */
interface CarouselImageProps
  extends MotionPropsFor<IntrinsicSystemProps<'div'>> {
  image: AiOrSampleDisplayImage;
  imagePair?: ImagePair;
  nextImage?: AiOrSampleGeneratedImage | null;
  placeholderSrc?: string;
  previousImage?: AiOrSampleGeneratedImage | null;
  referenceImage?: AiOrSampleGeneratedImage;
}

const MotionCenter = motion(Center);

export const CarouselImage = forwardRef<CarouselImageProps, 'div'>(
  (
    {
      image,
      imagePair,
      nextImage,
      placeholderSrc,
      previousImage,
      referenceImage,
      ...props
    },
    ref,
  ) => {
    const {
      primaryImage,
      toggleImage,
      loading: isToggleImageLoading,
    } = imagePair ?? {};

    const isCurrent = !!imagePair;

    const {
      carouselMotion,
      next,
      previous,
      isShowingOriginalImage,
      isLayoutAnimationComplete,
      onLayoutAnimationComplete,
    } = useFullscreenImageContents();

    const dragProps = isCurrent
      ? ({
          drag: 'x',
          style: { x: carouselMotion },
          dragSnapToOrigin: true,
          onDragEnd: onSwipe({
            axis: 'x',
            start: next,
            end: previous,
          }),
        } satisfies MotionImageProps)
      : {};

    const toggleDuration = useFullscreenImageBreakpointValue(0.25, 0.75);
    const isFullscreenImageOpen = useIsFullscreenImageOpen();
    const layoutTransition = useFullscreenTransition();

    const overrideLayoutTransition =
      isFullscreenImageOpen && isLayoutAnimationComplete;

    return (
      <MotionCenter
        boxSizing="border-box"
        position="absolute"
        top={0}
        {...props}
        ref={ref}
        sx={{
          '& img.current-image': { transform: 'translateX(0) !important' },
        }}
      >
        <system.div px={$gapPadding}>
          {/**
           * This element exists solely to facilitate the `layoutId` transition
           * from `FullscreenThumbnail` to the fullscreen view and back. We need
           * to selectively override unwanted `layoutId` transitions that happen
           * as the user pages through images and the `FullscreenImageThumbnail`
           * component for each image mounts and unmounts. This is accomplished
           * by overriding `opacity` and `transform` with `!important`.
           */}
          <motion.div
            layoutId={getFullscreenImageLayoutId(image)}
            onLayoutAnimationComplete={() => {
              latestFullscreenImage(image.aiGeneratedImage);
              onLayoutAnimationComplete();
            }}
            opacity={overrideLayoutTransition ? '1 !important' : undefined}
            transform={overrideLayoutTransition ? 'none !important' : undefined}
            transition={{ layout: layoutTransition }}
          >
            <GeneratedImage
              alt={primaryImage?.alt ?? ''}
              aria-hidden={!isCurrent}
              className="current-image"
              image={image.aiGeneratedImage}
              placeholderSrc={placeholderSrc}
              referenceImage={referenceImage}
              {...dragProps}
            />
          </motion.div>
        </system.div>

        <AnimatePresence initial={false}>
          {previousImage && isLayoutAnimationComplete && (
            <AdjacentImage
              image={previousImage}
              key="previous-image"
              left={0}
              position="absolute"
              referenceImage={referenceImage}
              transform="translateX(-100%)"
            />
          )}
          {nextImage && isLayoutAnimationComplete && (
            <AdjacentImage
              image={nextImage}
              key="next-image"
              position="absolute"
              referenceImage={referenceImage}
              right={0}
              transform="translateX(100%)"
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {isShowingOriginalImage && (
            <>
              {toggleImage && (
                <motion.img
                  alt={toggleImage.alt}
                  animate={{ opacity: 1 }}
                  borderRadius={{ base: 0, [layoutBreakpoint]: '300' }}
                  exit={{ opacity: 0 }}
                  initial={{ opacity: 0 }}
                  key="original-image"
                  objectFit="cover"
                  position="absolute"
                  src={toggleImage.url}
                  transition={{ duration: toggleDuration }}
                  {...altStyle()}
                  {...toggleImagePosition}
                />
              )}
              <LoadingState
                borderRadius="200"
                color="neutral.0"
                isLoading={isToggleImageLoading}
                {...toggleImagePosition}
              />
            </>
          )}
        </AnimatePresence>
      </MotionCenter>
    );
  },
);
